import type { Action } from '@reduxjs/toolkit';
import isEmpty from 'lodash/fp/isEmpty';
import { type Dispatch, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { config } from '../../config';
import { useAppDispatch } from '../../configuration/setup/hooks';
import { storage } from '../../configuration/setup/storage';
import { chapters } from './chapters';
import {
    getServiceInfoChapterFromSearchParams,
    hasServiceInfoQueryParam,
    shouldShowServiceInfo,
} from './serviceInfoSearchParams';
import { serviceInfoDialogClosed, serviceInfoDialogOpenedAtChapter } from './serviceInfoSlice';

const RELEASE_VERSION_KEY = 'releaseVersion';
const ONBOARDING_TOUR_SEEN_KEY = 'hasRentalOnboardingTourBeenSeen';

const isNewVersion = (version: string) => {
    if (!version) {
        return true;
    }
    const localVersionNumbers = version.split('.');
    const currentVersionNumbers = config.releaseVersion.split('.');

    return !(
        currentVersionNumbers[0] === localVersionNumbers[0] &&
        currentVersionNumbers[1] === localVersionNumbers[1] &&
        currentVersionNumbers[2] === localVersionNumbers[2]
    );
};

export const triggerServiceInfoDialog = (dispatch: Dispatch<Action>, searchParams: URLSearchParams) => {
    if (hasServiceInfoQueryParam(searchParams)) {
        const serviceInfoChapter = getServiceInfoChapterFromSearchParams(searchParams);

        if (shouldShowServiceInfo(searchParams) && serviceInfoChapter) {
            dispatch(serviceInfoDialogOpenedAtChapter(serviceInfoChapter));
        } else {
            dispatch(serviceInfoDialogClosed());
        }
        return;
    }

    const releaseVersion = storage.load(RELEASE_VERSION_KEY);
    const hasRentalOnboardingTourBeenSeen = storage.load(ONBOARDING_TOUR_SEEN_KEY);
    const isDisabled = searchParams.get('test') === 'true';

    if (isDisabled || !hasRentalOnboardingTourBeenSeen) {
        return;
    }

    if (isEmpty(releaseVersion)) {
        dispatch(serviceInfoDialogOpenedAtChapter(chapters.whatsNew));
    }

    if (releaseVersion && isNewVersion(releaseVersion)) {
        dispatch(serviceInfoDialogOpenedAtChapter(chapters.whatsNew));
    }

    storage.save(RELEASE_VERSION_KEY, config.releaseVersion);
};

export const useServiceInfoDialogOnStart = () => {
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();

    useEffect(() => {
        triggerServiceInfoDialog(dispatch, searchParams);
    }, [dispatch, searchParams]);
};
